import { useElementsInitializer } from '@/modules/map-editor';
import type { MapAreaService } from '@/modules/map-editor';
import type { ElementsInitOptions } from '@/modules/map-editor';

/**
 * Composable for element lifecycle management.
 * Coordinates populating element data from the store and initializing
 * their off-screen canvas caches. This is the public API consumed by
 * components (CanvasEditor, MapInstance).
 *
 * @param mapAreaService - MapAreaService singleton used to trigger re-renders
 */
export function useElements(mapAreaService: MapAreaService) {
  const initializer = useElementsInitializer();

  /**
   * Initialize elements — populate data from the store (unless skipped)
   * and build off-screen canvas caches for every node and edge.
   *
   * @param options.avoidPopulate - When true, skip store dispatches and only rebuild caches
   */
  async function elementsInit(options: ElementsInitOptions = {}): Promise<void> {
    const { avoidPopulate = false } = options;

    if (!avoidPopulate) {
      await initializer.populateNodes();
      await initializer.populateEdges();
      await initializer.populateNodeRelatedEdges();
    }

    // Initialize off-screen caches
    initializer.initializeAllCaches();

    // Mark map for update so the animation loop picks up the change
    mapAreaService.updateElement(true);
  }

  return {
    elementsInit
  };
}
